/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';

import type { RemixOptionKind } from '../../../index.ts';

const messages = defineMessages({
	rovoIsThinking: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a remix',
	},
	analyzingContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content for a remix',
	},
	puttingPiecesTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content for a remix',
	},
	structuringRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.structuring-remix',
		defaultMessage: 'Structuring your remix',
		description: 'Loading message shown while Rovo is structuring the remix output',
	},
	designingRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.designing-remix',
		defaultMessage: 'Designing your remix',
		description: 'Loading message shown while Rovo is designing the remix output',
	},
	addingItAllTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements of the remix',
	},
	applyingFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing the remix',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating a remix',
	},
});

const rotatingMessagesNonRemixEntrance = defineMessages({
	rovoIsImagining: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-imagining',
		defaultMessage: 'Rovo is dreaming it up',
		description: 'Initial loading message shown while Rovo is starting to generate an image.',
	},
	sketchingSomeIdeas: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.sketching-some-ideas',
		defaultMessage: 'Sketching ideas',
		description: 'Loading message shown while Rovo is beginning to form the image.',
	},
	mixingTheColors: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.mixing-the-colors',
		defaultMessage: 'Mixing colors',
		description: 'Loading message shown while Rovo is adding color to the image.',
	},
	findingTheRightStyle: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.finding-the-right-style',
		defaultMessage: 'Finding the right style',
		description: 'Loading message shown while Rovo is refining the visual style.',
	},
	bringingItToLife: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.bringing-it-to-life',
		defaultMessage: 'Bringing it to life',
		description: 'Loading message shown while Rovo is rendering the image.',
	},
	addingTheDetails: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-the-details',
		defaultMessage: 'Adding details',
		description: 'Loading message shown while Rovo is adding finer details.',
	},
	applyingTheFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-the-final-touches',
		defaultMessage: 'Applying final touches',
		description: 'Loading message shown while Rovo is finalizing the image.',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.image-almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating the image.',
	},
});

const styles = cssMap({
	// Positioning only — RovoThinkingBarWrapper supplies the visual chrome.
	rovoWrapperFixed: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		minWidth: '400px',
	},
	rovoWrapperAbsolute: {
		position: 'absolute',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		minWidth: '400px',
	},
	rovoWrapperRelative: {
		position: 'relative',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		minWidth: '400px',
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	optionType?: RemixOptionKind;
	/** Render the bar with relative positioning instead of fixed/absolute overlay positioning. */
	isRelative?: boolean;
	/** Render a loading progress bar (and swap the animated border for a solid one). */
	showProgressBar?: boolean;
	/** When `showProgressBar`, rapidly fill to 100% then fire `onCompleteAnimationFinished`. */
	isComplete?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	/**
	 * The content type of the document being edited (e.g. `'jira.issue'`, `'confluence.page'`).
	 * When the content type indicates an inline editor context (e.g. a Jira issue comment),
	 * the bar is positioned absolutely within the editor wrapper rather than fixed to the viewport.
	 */
	contentType?: string;
	/**
	 * When true, uses the remix rotating titles.
	 * Remix entrance is the initial loading state when the user clicks the remix button.
	 * Non-remix entrance is using BE but not from remix
	 */
	isRemixEntrance?: boolean;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, a sequentially
 * rotating status text, an Esc keyboard hint, and a stop button. Matches the
 * visual style of the inline chat ThinkingView / AI loading screen.
 *
 * The status text progresses through a fixed ordered sequence:
 * "Rovo is thinking" → "Analyzing your content" → "Putting the pieces together"
 * → "Structuring your remix" → "Designing your remix" → "Adding it all together"
 * → "Applying the final touches" → "Almost done"
 */
export function RemixThinkingBar({
	onStop,
	optionType: _optionType,
	isRelative = false,
	showProgressBar = false,
	isComplete = false,
	onCompleteAnimationFinished,
	contentType,
	isRemixEntrance = true,
}: RemixThinkingBarProps): JSX.Element {
	const positionAbsolute = contentType === 'jira.issue';
	const { formatMessage } = useIntl();

	const thinkingTexts = [
		formatMessage(messages.rovoIsThinking),
		formatMessage(messages.analyzingContent),
		formatMessage(messages.puttingPiecesTogether),
		formatMessage(messages.structuringRemix),
		formatMessage(messages.designingRemix),
		formatMessage(messages.addingItAllTogether),
		formatMessage(messages.applyingFinalTouches),
		formatMessage(messages.almostDone),
	];

	const thinkingTextsNonRemixEntrance = [
		formatMessage(rotatingMessagesNonRemixEntrance.rovoIsImagining),
		formatMessage(rotatingMessagesNonRemixEntrance.sketchingSomeIdeas),
		formatMessage(rotatingMessagesNonRemixEntrance.mixingTheColors),
		formatMessage(rotatingMessagesNonRemixEntrance.findingTheRightStyle),
		formatMessage(rotatingMessagesNonRemixEntrance.bringingItToLife),
		formatMessage(rotatingMessagesNonRemixEntrance.addingTheDetails),
		formatMessage(rotatingMessagesNonRemixEntrance.applyingTheFinalTouches),
		formatMessage(rotatingMessagesNonRemixEntrance.almostDone),
	];

	return (
		<Box
			xcss={
				// eslint-disable-next-line no-nested-ternary
				isRelative
					? styles.rovoWrapperRelative
					: positionAbsolute
						? styles.rovoWrapperAbsolute
						: styles.rovoWrapperFixed
			}
			testId="remix-thinking-bar-gradient-border"
		>
			<RovoThinkingBarWrapper isThinking>
				<RovoThinkingBar
					onStop={onStop}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					useRotatingTitle
					customRotatingMessages={isRemixEntrance ? thinkingTexts : thinkingTextsNonRemixEntrance}
				/>
			</RovoThinkingBarWrapper>
		</Box>
	);
}
